<script setup>
import { ref } from "vue";
import { permanentSelfExclusion } from "@/app/api";
import { toast } from "@/app/utils/notification";
import { useI18n } from "@/app/composables/useI18n";
import { useAuth } from "@/user-account/composables/useAuth";
import { useBetStore } from "/store";
import BaseButton from "@/app/components/ui/BaseButton.vue";

const { t } = useI18n();
const { logout } = useAuth();
const store = useBetStore();

const showConfirm = ref(false);
const isLoading = ref(false);

function openConfirmModal() {
  showConfirm.value = true;
}

function closeConfirmModal() {
  showConfirm.value = false;
}

async function confirmSelfExclusion() {
  try {
    isLoading.value = true;
    
        await permanentSelfExclusion();
    
    toast.info(t("self_exclusion.permanent_success_message"));
    store.handleShowBetModal(false);
    logout();
  } catch (error) {
    console.error("Failed to self-exclude:", error);
    if (error.response?.status === 400) {
      toast.error(error.response.data.message || t("self_exclusion.permanent_error_message"));
    } else {
      toast.error(t("self_exclusion.permanent_error_message"));
    }
  } finally {
    isLoading.value = false;
  }
  
  showConfirm.value = false;
}
</script>

<template>
  <div class="container">
    <div class="form">
      <span class="title">
        {{ t("self_exclusion.permanent_title") }}
      </span>
      
      <div class="content">
        <div class="description">
          <p>
            {{ t("self_exclusion.permanent_description") }}
          </p>
          <p>
            {{ t("self_exclusion.permanent_support") }}
          </p>
        </div>
        
        <BaseButton 
          class="self-exclude-btn danger wide high" 
          type="button" 
          :disabled="isLoading"
          @click="openConfirmModal"
        >
          {{ t("self_exclusion.permanently_exclude") }}
        </BaseButton>
      </div>
    </div>

    <!-- Confirmation Modal -->
    <Transition name="modal">
      <div v-if="showConfirm" class="modal-mask">
        <div class="modal-container">
          <div class="modal-detail">
            <div class="close-btn" @click="closeConfirmModal">
              <div>{{ t("general.close") }}</div>
              <img src="/assets/images/close-modal-icon.svg" alt="" />
            </div>
                        <div class="modal-body">
              <div class="modal-title">
                {{ t('self_exclusion.confirm_title') }}
              </div>
              <div class="modal-content">
                <p>
                  {{ t('self_exclusion.confirm_message_1') }}
                </p>
                <p>
                  {{ t('self_exclusion.confirm_message_2') }}
                </p>
                <p class="warning">
                  {{ t('self_exclusion.confirm_warning') }}
                </p>
              </div>
              <div class="modal-actions">
                <BaseButton 
                  class="confirm-btn danger wide high" 
                  type="button" 
                  :disabled="isLoading"
                  @click="confirmSelfExclusion"
                >
                  {{ t("self_exclusion.confirm_button") }}
                </BaseButton>
                <BaseButton 
                  class="cancel-btn secondary wide high" 
                  type="button" 
                  :disabled="isLoading"
                  @click="closeConfirmModal"
                >
                  {{ t("common.cancel") }}
                </BaseButton>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style lang="scss" scoped>
  .container {
.form {
  border-radius: 8px;
  background: var(--be-big-card-bg);
  padding: 16px;
  margin-bottom: 1rem;

    .title {
      font-size: 14px;
      font-weight: 600;
      color: var(--be-primary-text-color);
      margin-bottom: 16px;
    }

    .content {
      .description {
        margin-bottom: 20px;
        
        p {
          margin-bottom: 12px;
          font-size: 14px;
          line-height: 1.5;
          color: var(--be-text-secondary);
          
          &:last-child {
            margin-bottom: 0;
          }
        }
      }

      .self-exclude-btn {
        &.danger {
          background: #f44336;
          color: white;
  border: none;

          &:hover {
            background: #d32f2f;
          }

          &:disabled {
            background: #ffcdd2;
            cursor: not-allowed;
          }
        }
      }
    }
  }
}

/* Modal styles */
.modal-mask {
  position: fixed;
  z-index: 9998;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  backdrop-filter: blur(5px);
  display: flex;
  transition: opacity 0.3s ease;
}

.modal-container {
  width: 491px;
  margin: auto;
  padding: 39px 20px 20px 20px;
  height: 100%;
  overflow: auto;
  transition: all 0.3s ease;
  position: relative;
  display: flex;
}

.modal-detail {
  background: var(--be-bg);
  padding: 20px 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.33);
  margin-top: 13px;
  border-radius: 20px;
  margin: auto;
  position: relative;
  width: 100%;
}

.close-btn {
  color: white;
  position: absolute;
  top: -32px;
  right: 3px;
  display: flex;
  gap: 0 5px;
  cursor: pointer;
}

.modal-body {
  border-radius: 8px;
  background: var(--be-big-card-bg);
  padding: 20px;

.modal-title {
    font-size: 1.4em;
  font-weight: 700;
    margin-bottom: 20px;
    color: var(--be-primary-text-color);
  }

  .modal-content {
    margin-bottom: 24px;
    
    p {
      margin-bottom: 12px;
      font-size: 14px;
      line-height: 1.5;
      color: var(--be-text-secondary);
      
      &.warning {
        color: #f44336;
        font-weight: 600;
      }
      
      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  .modal-actions {
    display: flex;
    gap: 12px;
    flex-direction: column;

    .confirm-btn {
      &.danger {
        background: #f44336;
        color: white;
        border: none;

        &:hover {
          background: #d32f2f;
        }

        &:disabled {
          background: #ffcdd2;
          cursor: not-allowed;
        }
      }
    }

    .cancel-btn {
      &.secondary {
        background: var(--be-secondary-btn);
        color: var(--be-secondary-btn-label);
        border: 1px solid var(--be-border-color);

        &:hover {
          background: var(--be-secondary-btn-hover);
        }

        &:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      }
    }
  }
}

/* Modal transition */
.modal-enter-active, .modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from, .modal-leave-to {
  opacity: 0;
}

@media screen and (max-width: 500px) {
  .modal-container {
    width: 95%;
    padding: 20px 10px;
  }
  
  .modal-actions {
    flex-direction: column;
    gap: 8px;
  }
}
</style>